import {
  ref as dbRef,
  onChildAdded,
  onChildChanged,
  onChildRemoved,
  query,
  orderByChild,
  limitToLast,
  get,
} from 'firebase/database';
import { db } from 'utils/firebase-config';
import { getMessageTime } from 'helpers/utils';

/**
 * Shared, module-level message cache keyed by room.
 *
 * One entry per room, ref-counted Firebase subscriptions, and an LRU that keeps recently
 * visited rooms live. Switching to a cached room renders instantly and reconciles in the
 * background (stale-while-revalidate).
 *
 * Pagination uses a GROWING WINDOW (`limitToLast(windowSize)`) rather than a timestamp
 * cursor. A cursor (`endBefore(oldestTimestamp)`) is unusable here because messages
 * written before `serverTimestamp()` was introduced have no `timestamp` field at all —
 * they sort as `null` under `orderByChild('timestamp')`, so a numeric cursor derived from
 * `createdTime` could silently return duplicates or skip rows. Growing the window always
 * yields the newest N in chronological order, legacy rows included, and makes `hasMore`
 * trivially correct: if Firebase returns fewer rows than we asked for, we've reached the
 * beginning of the room.
 */

const INITIAL_LOAD_LIMIT = 10;
const PAGE_SIZE = 20;
/** Rooms kept subscribed after their last consumer unmounts. */
const KEEP_ALIVE_ROOMS = 10;

export type RoomEntry = {
  pk: number;
  /** Keyed by message id, for O(1) child_added/changed/removed. */
  map: Map<string, any>;
  /** Sorted, immutable snapshot. Replaced (never mutated) so React sees a new identity. */
  list: any[];
  /** How many of the newest messages we currently request. Grows as you scroll up. */
  windowSize: number;
  hasMore: boolean;
  /** Has this room ever finished its initial load? Drives the spinner. */
  initialized: boolean;
  isLoadingMore: boolean;
  refCount: number;
  attached: boolean;
  cancelled: boolean;
  unsubscribers: Array<() => void>;
  subscribers: Set<() => void>;
};

const rooms = new Map<number, RoomEntry>();
/** pks of rooms with refCount === 0, oldest first. */
const keepAlive: number[] = [];

const sortByTime = (map: Map<string, any>): any[] =>
  Array.from(map.values()).sort((a, b) => getMessageTime(a) - getMessageTime(b));

/** Publish a new immutable snapshot to React subscribers. */
const emit = (entry: RoomEntry) => {
  entry.list = sortByTime(entry.map);
  entry.subscribers.forEach((notify) => notify());
};

const messagesPath = (pk: number) => dbRef(db, `chats/${pk}/messages`);

/** Newest `windowSize` messages, oldest-first within the window. */
const windowQuery = (pk: number, windowSize: number) =>
  query(messagesPath(pk), orderByChild('timestamp'), limitToLast(windowSize));

const ensureEntry = (pk: number): RoomEntry => {
  let entry = rooms.get(pk);
  if (!entry) {
    entry = {
      pk,
      map: new Map(),
      list: [],
      windowSize: INITIAL_LOAD_LIMIT,
      hasMore: true,
      initialized: false,
      isLoadingMore: false,
      refCount: 0,
      attached: false,
      cancelled: false,
      unsubscribers: [],
      subscribers: new Set(),
    };
    rooms.set(pk, entry);
  }
  return entry;
};

const detachListeners = (entry: RoomEntry) => {
  entry.unsubscribers.forEach((unsubscribe) => unsubscribe());
  entry.unsubscribers = [];
};

/**
 * (Re)bind live listeners to the current window. Called again after each page so the
 * live range always covers everything we've loaded.
 */
const attachListeners = (entry: RoomEntry) => {
  detachListeners(entry);
  const scoped = windowQuery(entry.pk, entry.windowSize);

  entry.unsubscribers = [
    onChildAdded(scoped, (snap) => {
      const message = snap.val();
      if (!message || Object.keys(message).length === 0) return;
      // Re-binding replays every child in the window; only new ids are interesting.
      if (entry.map.has(message.id)) return;
      entry.map.set(message.id, message);
      emit(entry);
    }),

    onChildChanged(scoped, (snap) => {
      const message = snap.val();
      if (!message || Object.keys(message).length === 0) return;
      entry.map.set(message.id, message);
      emit(entry);
    }),

    onChildRemoved(scoped, (snap) => {
      const id = snap.val()?.id ?? snap.key;
      if (!id) return;
      // A limitToLast() query fires child_removed when a message merely falls OUT of the
      // window (i.e. a newer one arrived), not only when it's deleted. Deleting on that
      // signal silently erodes paginated history, so confirm the node is really gone.
      get(dbRef(db, `chats/${entry.pk}/messages/${id}`))
        .then((exists) => {
          if (exists.exists()) return;
          if (entry.map.delete(id)) emit(entry);
        })
        .catch(() => {
          /* transient read failure: keep the message rather than lose it */
        });
    }),
  ];
};

const attach = (entry: RoomEntry) => {
  if (entry.attached) return;
  entry.attached = true;
  entry.cancelled = false;

  get(windowQuery(entry.pk, entry.windowSize))
    .then((snapshot) => {
      if (entry.cancelled) return;

      snapshot.forEach((child) => {
        const message = child.val();
        if (message && Object.keys(message).length > 0) {
          entry.map.set(message.id, message);
        }
      });

      // Fewer rows than requested means we already hold the whole room.
      entry.hasMore = snapshot.size === entry.windowSize;
      entry.initialized = true;
      emit(entry);
      attachListeners(entry);
    })
    .catch((error) => {
      if (entry.cancelled) return;
      console.error('Error loading messages:', error);
      entry.initialized = true; // unblock the UI even on failure
      emit(entry);
    });
};

const teardown = (entry: RoomEntry) => {
  entry.cancelled = true;
  entry.attached = false;
  detachListeners(entry);
};

const evictIfCold = () => {
  while (keepAlive.length > KEEP_ALIVE_ROOMS) {
    const pk = keepAlive.shift();
    if (pk === undefined) return;
    const entry = rooms.get(pk);
    if (entry && entry.refCount === 0) {
      teardown(entry);
      rooms.delete(pk);
    }
  }
};

export const getRoomEntry = (pk?: number): RoomEntry | undefined =>
  pk ? rooms.get(pk) : undefined;

/**
 * Subscribe to a room's messages. The first subscriber attaches the Firebase listeners;
 * when the last one leaves, the room is parked in the LRU (still live) rather than torn
 * down, so coming back is instant *and* up to date.
 */
export const subscribeMessages = (pk: number, onChange: () => void): (() => void) => {
  const entry = ensureEntry(pk);

  entry.subscribers.add(onChange);
  entry.refCount += 1;

  const parked = keepAlive.indexOf(pk);
  if (parked !== -1) keepAlive.splice(parked, 1);

  attach(entry);

  return () => {
    entry.subscribers.delete(onChange);
    entry.refCount = Math.max(0, entry.refCount - 1);
    if (entry.refCount === 0 && !keepAlive.includes(pk)) {
      keepAlive.push(pk);
      evictIfCold();
    }
  };
};

/**
 * Grow the window by one page and rebuild the room's cache from it. Repeated calls walk
 * back through the entire history; `hasMore` goes false once Firebase returns fewer rows
 * than we asked for.
 */
export const loadOlderMessagesForRoom = async (pk: number): Promise<void> => {
  const entry = rooms.get(pk);
  // `initialized` matters: before the first load lands, growing the window is pointless.
  if (!entry || !entry.initialized || !entry.hasMore || entry.isLoadingMore) return;

  entry.isLoadingMore = true;
  emit(entry);

  const nextWindow = entry.windowSize + PAGE_SIZE;

  try {
    const snapshot = await get(windowQuery(pk, nextWindow));

    // The room may have been evicted while this was in flight.
    if (rooms.get(pk) !== entry || entry.cancelled) return;

    const merged = new Map<string, any>();
    snapshot.forEach((child) => {
      const message = child.val();
      if (message && Object.keys(message).length > 0) {
        merged.set(message.id, message);
      }
    });

    entry.map = merged;
    entry.windowSize = nextWindow;
    // Based on what the query RETURNED, not on how many were new. Counting only
    // newly-seen rows would latch hasMore to false the first time a page deduped.
    entry.hasMore = snapshot.size === nextWindow;

    // Keep the live range covering everything we now hold.
    attachListeners(entry);
  } catch (error) {
    console.error('Error loading older messages:', error);
  } finally {
    if (rooms.get(pk) === entry) {
      entry.isLoadingMore = false;
      emit(entry);
    }
  }
};
